import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Search, Trash2, ShieldAlert } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMaintenance() {
  const [email, setEmail] = useState('');
  const [match, setMatch] = useState('exact');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const runPreview = async () => {
    if (!email.trim()) return toast.error('Enter an email or pattern');
    setLoading(true);
    setPreview(null);
    try {
      const r = await axios.post(`${API_URL}/api/admin/maintenance/delete-by-email?dry_run=true`,
        { email: email.trim(), match }, { withCredentials: true });
      setPreview(r.data);
      if (r.data.total === 0) toast.info('No matching records found');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const runDelete = async () => {
    if (!preview || preview.total === 0) return;
    if (!window.confirm(`Permanently delete ${preview.total} records for "${email.trim()}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/maintenance/delete-by-email?dry_run=false`,
        { email: email.trim(), match }, { withCredentials: true });
      toast.success(`Deleted ${r.data.total} records`);
      setPreview({ ...r.data, would_delete: r.data.deleted, dry_run: true, _done: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };

  const rows = preview ? Object.entries(preview.would_delete || {}).filter(([, v]) => v > 0) : [];

  return (
    <AdminLayout title="Maintenance">
      <div data-testid="admin-maintenance-page" className="max-w-2xl">
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="text-[#F97316]" size={24} />
          <h1 className="heading-2 text-2xl text-[#0A0F1C]">Maintenance — Delete by Email</h1>
        </div>
        <p className="text-sm text-[#475569] mb-6">
          Permanently remove a person's records across every collection (leads, bookings, downloads, coach CRM, webinar registrations, payments, and more). Always preview first. Protected admin/owner emails are refused.
        </p>

        <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4 mb-6 flex gap-3" data-testid="maintenance-warning">
          <AlertTriangle className="text-[#EA580C] shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-[#9A3412]">This action is irreversible. Records are deleted immediately and are not recoverable.</div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
          <input
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
            placeholder="person@example.com"
            aria-label="email to delete"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setPreview(null); }}
            data-testid="maintenance-email-input"
          />
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-1.5 text-[#475569]">
              <input type="radio" name="match" checked={match === 'exact'} onChange={() => { setMatch('exact'); setPreview(null); }} data-testid="match-exact" /> Exact email
            </label>
            <label className="flex items-center gap-1.5 text-[#475569]">
              <input type="radio" name="match" checked={match === 'contains'} onChange={() => { setMatch('contains'); setPreview(null); }} data-testid="match-contains" /> Contains (pattern)
            </label>
          </div>
          <button
            onClick={runPreview}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
            data-testid="maintenance-preview"
          >
            <Search size={15} />{loading ? 'Scanning…' : 'Preview matching records'}
          </button>
        </div>

        {preview && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mt-4" data-testid="maintenance-preview-result">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-[#0A0F1C]">{preview._done ? 'Deleted' : 'Found'} {preview.total} record{preview.total === 1 ? '' : 's'}</h2>
              <span className="text-xs text-[#94A3B8]">{preview.email} · {preview.match}</span>
            </div>
            {rows.length > 0 ? (
              <div className="divide-y border-t border-[#F1F5F9]">
                {rows.map(([coll, count]) => (
                  <div key={coll} className="flex justify-between py-1.5 text-sm" data-testid={`mnt-row-${coll}`}>
                    <span className="text-[#475569]">{coll.replace(/_/g, ' ')}</span>
                    <span className="font-bold text-[#0A0F1C]">{count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[#94A3B8]">No matching records.</p>}

            {!preview._done && preview.total > 0 && (
              <button
                onClick={runDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium mt-4 disabled:opacity-60"
                data-testid="maintenance-delete"
              >
                <Trash2 size={15} />{deleting ? 'Deleting…' : `Delete ${preview.total} records permanently`}
              </button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
